import React from "react";
import { CheckCircle2, Download } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { fmtCAD, fmtInt, downloadReceipt } from "@/lib/billing";

/* Celebratory, brief confirmation shown after a cart charge or a return from the secure page. */
export default function TransactionApprovedDialog({ open, onOpenChange, txn, mode }) {
  if (!txn) return null;
  const lines = txn.line_items || [];
  const byPool = lines.reduce((a, li) => { a[li.pool] = (a[li.pool] || 0) + Number(li.credits || 0); return a; }, {});
  const isSub = !lines.length && (txn.kind === "subscription" || txn.kind === "grant");

  const dl = async () => {
    try { await downloadReceipt(txn.receipt_id, txn.receipt_number); }
    catch { toast.error("Couldn't download the receipt"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="transaction-approved-dialog">
        <DialogTitle className="sr-only">Transaction approved</DialogTitle>
        <div className="flex flex-col items-center gap-3 pt-2 text-center">
          <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center animate-in zoom-in duration-300">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 font-display" data-testid="approved-title">Transaction approved</h2>
          <p className="text-sm text-slate-600">{mode === "charged" ? "Charged to your card on file." : "Payment received — thank you!"}</p>

          <div className="w-full rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-left">
            {isSub ? (
              <div data-testid="approved-sub">Your subscription is active{txn.period_label ? ` · ${txn.period_label}` : ""}.</div>
            ) : (
              <ul className="space-y-1" data-testid="approved-credits">
                {Object.entries(byPool).map(([pool, c]) => (
                  <li key={pool} className="flex items-center justify-between">
                    <span className="text-slate-600 capitalize">{pool} credits added</span>
                    <span className="font-semibold text-slate-900 tabular-nums">+{fmtInt(c)}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex items-center justify-between border-t border-slate-200 mt-2 pt-2">
              <span className="text-slate-600">Amount charged</span>
              <span className="font-bold text-slate-900 tabular-nums" data-testid="approved-amount">{fmtCAD(txn.total_cad)}</span>
            </div>
            {txn.card_brand || txn.card_last4 ? (
              <div className="text-[11px] text-slate-500 mt-1 text-right">{txn.card_brand || "Card"} •••• {txn.card_last4 || "????"}</div>
            ) : null}
          </div>

          <div className="flex items-center gap-2 w-full">
            {txn.receipt_id ? (
              <Button variant="outline" className="flex-1 min-h-[44px]" onClick={dl} data-testid="approved-receipt">
                <Download className="h-4 w-4 mr-2" /> {txn.receipt_number || "Receipt"}
              </Button>
            ) : null}
            <Button className="flex-1 text-white min-h-[44px]" style={{ background: "hsl(var(--brand))" }}
              onClick={() => onOpenChange(false)} data-testid="approved-done">Done</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
